<template>
    <el-dialog
        v-model="state.visible"
        :title="t('personnelList.importMachinistTitle')"
        width="720"
        align-center
        draggable
        @closed="resetDialog"
    >
        <div class="machinist-import">
            <p class="machinist-import__tip">{{ t('personnelList.importMachinistTip') }}</p>
            <div class="machinist-import__actions">
                <el-button type="primary" link @click="downloadTemplate">
                    {{ t('personnelList.importDownloadTemplate') }}
                </el-button>
            </div>
            <el-upload
                ref="uploadRef"
                drag
                :auto-upload="false"
                :limit="1"
                accept=".xls,.xlsx"
                :on-change="handleFileChange"
                :on-exceed="handleExceed"
            >
                <div class="machinist-import__upload-inner">
                    <span>{{ t('personnelList.importDragTip') }}</span>
                </div>
            </el-upload>

            <div v-if="state.result" class="machinist-import__result">
                <p>{{ state.result.msg }}</p>
                <el-table :data="state.result.rows" size="small" max-height="280" border>
                    <el-table-column prop="row" :label="t('personnelList.importColRow')" width="70" />
                    <el-table-column prop="username" :label="t('personnelList.colUsername')" width="120" />
                    <el-table-column prop="success" :label="t('personnelList.importColStatus')" width="90">
                        <template #default="{ row }">
                            <el-tag :type="row.success ? 'success' : 'danger'" size="small">
                                {{ row.success ? t('personnelList.importSuccess') : t('personnelList.importFailed') }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="msg" :label="t('personnelList.importColMessage')" min-width="180" />
                </el-table>
            </div>
        </div>
        <template #footer>
            <el-button @click="state.visible = false">{{ t('common.cancel') }}</el-button>
            <el-button type="primary" :loading="state.uploading" @click="submitImport">
                {{ t('personnelList.importSubmit') }}
            </el-button>
        </template>
    </el-dialog>
</template>

<script setup>
import { reactive, ref, defineEmits, defineExpose } from 'vue'
import { ElMessage } from 'element-plus'
import { genFileId } from 'element-plus'
import { usePageI18n, t } from '@/composables/usePageI18n.js'
import { exportExcel } from '@/utils/apiBase'
import { importMachinistExcelApi } from '@/api/system/personnel'

const emit = defineEmits(['refresh'])
const uploadRef = ref()
const state = reactive({
    visible: false,
    uploading: false,
    file: null,
    result: null,
})

usePageI18n(() => {})

const open = () => {
    state.visible = true
}

const resetDialog = () => {
    state.uploading = false
    state.file = null
    state.result = null
    uploadRef.value?.clearFiles()
}

const downloadTemplate = async () => {
    const { ok, msg } = await exportExcel('Excel/machinistImportTemplate', {}, '机手批量导入模板.xls')
    if (!ok) {
        ElMessage.error(msg || t('personnelList.importTemplateFailed'))
    }
}

const handleFileChange = (uploadFile) => {
    state.file = uploadFile?.raw || null
    state.result = null
}

const handleExceed = (files) => {
    uploadRef.value?.clearFiles()
    const file = files[0]
    file.uid = genFileId()
    uploadRef.value?.handleStart(file)
    state.file = file
    state.result = null
}

const submitImport = async () => {
    if (!state.file) {
        ElMessage.warning(t('personnelList.importSelectFile'))
        return
    }
    state.uploading = true
    try {
        const res = await importMachinistExcelApi(state.file)
        if (res?.code === 200) {
            state.result = {
                msg: res.msg,
                rows: res.data?.rows || [],
            }
            ElMessage.success(res.msg || t('common.success'))
            emit('refresh')
        } else {
            ElMessage.error(res?.msg || t('common.failed'))
        }
    } catch (e) {
        ElMessage.error(t('common.failed'))
    } finally {
        state.uploading = false
    }
}

defineExpose({ open })
</script>

<style lang="less" scoped>
.machinist-import__tip {
    margin: 0 0 12px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.6;
}

.machinist-import__actions {
    margin-bottom: 12px;
}

.machinist-import__upload-inner {
    padding: 24px 0;
    color: #64748b;
}

.machinist-import__result {
    margin-top: 16px;

    p {
        margin: 0 0 10px;
        font-weight: 600;
    }
}
</style>
